// 酪蛋白产业链数字化（2221 版 · 7680×2160）
// 按 2223 数字看板副本的内容/结构移植，按 2221 大屏基准 7680×2160 重排：
//   - 横向尺寸 ×2（2221 比 2223 宽一倍），字号 ×2（避免 LED 墙上文字显得过小）
//   - 纵向结构保持（两版高度同为 2160），top 预留 --screen-margin 给顶部标题、
//     底部预留给导航条；层间用 flex 自适应压缩，确保整页不溢出
// 布局：智慧决策/系统集成/MOM/平台层/网络传输/设备层 + 左层级标签 + 右安全控制
// 2026-09-29 调整：删除页内"酪蛋白产业链数字化"标题；整体缩小标题与文字以缓解高度挤压
<template>
  <div class="subpage industry-chain" :style="rootVars">
    <div class="ic-page">
      <div class="ic-body">
        <!-- 左侧层级标签栏（挂载后按中间各层实测位置绝对定位，保证逐像素对齐） -->
        <div class="ic-side ic-side-left" ref="sideLeft">
          <div
            class="ic-layer-tag"
            v-for="(l, i) in sideLabels"
            :key="l.name"
            ref="tags"
            :style="tagStyles[i] || {}"
          >
            <span class="ic-layer-tag-text">
              <span class="ic-vcol" v-for="(c, ci) in (tagCols[i] || [l.name])" :key="ci">{{ c }}</span>
            </span>
          </div>
        </div>

        <!-- 中间主架构区 -->
        <div class="ic-main">
          <!-- 1 智慧决策 -->
          <div ref="layers" class="ic-layer ic-decision" :style="{flex: sideLabels[0].flex}">
            <div class="ic-decision-row">
              <div class="ic-decision-item" v-for="item in decisionItems" :key="item">
                <div
                  class="ic-decision-card"
                  :class="{ clickable: isClickable(item) }"
                  @click="goSub(item)"
                >
                  <span class="ic-decision-name">{{ item }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="ic-arrows"><i class="a-up"></i><i class="a-line"></i><i class="a-down"></i></div>

          <!-- 2 系统集成 -->
          <div ref="layers" class="ic-layer ic-system" :style="{flex: sideLabels[1].flex}">
            <div class="ic-system-row">
              <div class="ic-system-bar" v-for="s in systems" :key="s">{{ s }}</div>
            </div>
          </div>

          <div class="ic-arrows"><i class="a-up"></i><i class="a-line"></i><i class="a-down"></i></div>

          <!-- 3 MOM 制造运营系统 -->
          <div ref="layers" class="ic-layer ic-mom" :style="{flex: sideLabels[2].flex}">
            <div class="ic-mom-row">
              <div class="ic-mom-group" v-for="g in momGroups" :key="g.title" :style="{flex: g.cols.length}">
                <div class="ic-mom-title">{{ g.title }}</div>
                <div class="ic-mom-cols">
                  <div class="ic-mom-col" v-for="(col, ci) in g.cols" :key="ci">
                    <div
                      class="ic-cell"
                      :class="{ clickable: isClickable(item) }"
                      v-for="item in col"
                      :key="item"
                      @click="goSub(item)"
                    >{{ item }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="ic-arrows"><i class="a-up"></i><i class="a-line"></i><i class="a-down"></i></div>

          <!-- 4 平台层 -->
          <div ref="layers" class="ic-layer ic-platform" :style="{flex: sideLabels[3].flex}">
            <div class="ic-platform-brand">{{ $C('industryChain','platformBrandA','工业物联') }}<br/>{{ $C('industryChain','platformBrandB','网平台') }}</div>
            <div class="ic-platform-right">
              <div class="ic-platform-row">
                <div class="ic-cell ic-cell-plain" v-for="p in platformItems" :key="p">{{ p }}</div>
              </div>
              <div class="ic-platform-infra">{{ infraText }}</div>
            </div>
          </div>

          <div class="ic-arrows"><i class="a-up"></i><i class="a-line"></i><i class="a-down"></i></div>

          <!-- 5 网络传输 -->
          <div ref="layers" class="ic-layer ic-network" :style="{flex: sideLabels[4].flex}">
            <div class="ic-network-row">
              <div class="ic-net-item" v-for="n in networkItems" :key="n">{{ n }}</div>
            </div>
          </div>

          <div class="ic-arrows"><i class="a-up"></i><i class="a-line"></i><i class="a-down"></i></div>

          <!-- 6 设备层 -->
          <div ref="layers" class="ic-layer ic-device" :style="{flex: sideLabels[5].flex}">
            <div class="ic-device-row">
              <div class="ic-cell ic-cell-plain" v-for="d in deviceItems" :key="d">{{ d }}</div>
            </div>
          </div>
        </div>

        <!-- 右侧安全控制竖条（挂载后按所覆盖层实测位置绝对定位：统一=上5层，厂家=设备层） -->
        <div class="ic-side ic-side-right">
          <div class="ic-guard" ref="guard1" :style="guard1Style">
            <span class="ic-guard-text">
              <span class="ic-vcol" v-for="(c, ci) in guard1Cols" :key="ci">{{ c }}</span>
            </span>
          </div>
          <div class="ic-guard ic-guard-factory" ref="guard2" :style="guard2Style">
            <span class="ic-guard-text">
              <span class="ic-vcol" v-for="(c, ci) in guard2Cols" :key="ci">{{ c }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 可点击进入下级页面的标签（其余项为内容展示，暂未独立成页）
const CLICKABLE = ['产品研发', '销售分析', '下游客户', '原料收购', '生产安全', '工艺流程'];

import {
  SIDE_LABELS, DECISION_ITEMS, SYSTEMS, MOM_GROUPS, PLATFORM_ITEMS,
  INFRA_TEXT, NETWORK_ITEMS, DEVICE_ITEMS, GUARD1_TEXT, GUARD2_TEXT,
} from '@/data/industryChain'
import { mediaUrl } from '@/cms/config'

export default {
  name: 'industryChain',
  data() {
    return {
      // 两侧列锚定样式（挂载后按中间各层实测位置计算）
      tagStyles: [],
      guard1Style: {},
      guard2Style: {},
      // 竖排文字分列结果（字多放不下时按两列竖排，保证与「设备层」同字号）
      tagCols: [],
      guard1Cols: ['统一安全控制'],
      guard2Cols: ['厂家安全控制'],
    };
  },
  computed: {
    /* ---- 数据层接入 CMS：后台「酪蛋白产业链数字化」可直接维护，未配置时用内置默认 ---- */
    sideLabels() { return this.$CList('industryChain', 'sideLabels', SIDE_LABELS) },
    decisionItems() { return this.$CList('industryChain', 'decisionItems', DECISION_ITEMS) },
    systems() { return this.$CList('industryChain', 'systems', SYSTEMS) },
    momGroups() { return this.$CList('industryChain', 'momGroups', MOM_GROUPS) },
    platformItems() { return this.$CList('industryChain', 'platformItems', PLATFORM_ITEMS) },
    infraText() { return this.$C('industryChain', 'infraText', INFRA_TEXT) },
    networkItems() { return this.$CList('industryChain', 'networkItems', NETWORK_ITEMS) },
    deviceItems() { return this.$CList('industryChain', 'deviceItems', DEVICE_ITEMS) },
    guard1Text() { return this.$C('industryChain', 'guard1Text', GUARD1_TEXT) },
    guard2Text() { return this.$C('industryChain', 'guard2Text', GUARD2_TEXT) },
    // 页面背景图（后台可配；未配时为透明，沿用全局舞台背景）
    rootVars() {
      const bg = this.$C('industryChain', 'pageBg', '')
      return bg ? { '--page-bg': `url(${mediaUrl(bg)}) center/cover no-repeat #030b1e` } : {}
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.pinSides();
      // 字体/图片等异步渲染完成后复测一次
      setTimeout(() => this.pinSides(), 400);
    });
  },
  activated() {
    this.$nextTick(() => this.pinSides());
  },
  methods: {
    // 将左侧标签与右侧安全控制竖条钉到中间对应层的实测位置（逐像素对齐）
    pinSides() {
      const layers = this.$el ? Array.prototype.slice.call(this.$el.querySelectorAll('.ic-main > .ic-layer')) : null;
      const tags = this.$el ? Array.prototype.slice.call(this.$el.querySelectorAll('.ic-side-left .ic-layer-tag')) : null;
      const base = this.$refs.sideLeft;
      if (!layers || layers.length < 6 || !tags || tags.length < 6 || !base) return;
      const l0r = layers[0].getBoundingClientRect();
      const scale = l0r.height / layers[0].offsetHeight || 1; // 全局 transform 缩放补偿
      const baseTop = base.getBoundingClientRect().top;
      const rel = el => {
        const b = el.getBoundingClientRect();
        return { top: (b.top - baseTop) / scale, h: b.height / scale };
      };
      const pos = layers.map(el => rel(el));

      // 竖排标题统一字号：以「设备层」标签（3 字）为基准推算，
      // 全页 6 个层级标签 + 2 个安全控制条保持同一字号，避免大小参差
      const devH = pos[5].h;
      const baseFs = Math.floor(Math.min(64, Math.max(20, (devH * 0.9) / (3 * 1.22))));
      const ls = Math.round(baseFs * 0.22);
      const advance = baseFs + ls; // 竖排每字实际占位 = 字号 + 字距
      const vStyle = { fontSize: baseFs + 'px', letterSpacing: ls + 'px' };

      // 同字号下若一列放不下，自动拆成两列竖排（上限两列，避免拆成一列一个字），
      // 保证文字完整不被裁切
      const splitCols = (text, h) => {
        const n = text.length;
        const maxPerCol = Math.max(1, Math.floor((h * 0.92) / advance));
        if (n <= maxPerCol) return [text];
        let cols = Math.min(2, Math.ceil(n / maxPerCol));
        while (cols < 2 && Math.ceil(n / cols) * advance > h * 0.92) cols++;
        const per = Math.ceil(n / cols);
        const out = [];
        for (let i = 0; i < n; i += per) out.push(text.slice(i, i + per));
        return out;
      };

      this.tagCols = pos.map((p, i) => splitCols(this.sideLabels[i].name, p.h));
      this.tagStyles = pos.map((p, i) => Object.assign(
        { top: p.top + 'px', height: p.h + 'px' },
        vStyle
      ));
      // 统一安全控制 = 智慧决策顶部 ~ 网络传输底部（上5层）；厂家安全控制 = 设备层
      const net = pos[4], dev = pos[5];
      const g1H = net.top + net.h - pos[0].top;
      this.guard1Cols = splitCols(this.guard1Text, g1H);
      this.guard1Style = Object.assign({ top: pos[0].top + 'px', height: g1H + 'px' }, vStyle);
      this.guard2Cols = splitCols(this.guard2Text, dev.h);
      this.guard2Style = Object.assign({ top: dev.top + 'px', height: dev.h + 'px' }, vStyle);
    },
    isClickable(item) {
      return CLICKABLE.indexOf(item) > -1;
    },
    goSub(item) {
      if (!this.isClickable(item)) return;
      // 六个专项页面全部接入真实下级页（from=industry-chain 供下级页定向返回）
      const SUB_PAGE = {
        '原料收购': '/qula-acquisition',    // 曲拉收购可视化平台
        '销售分析': '/sell',                // 康巴圣德经营数据
        '下游客户': '/downstream-heat',      // 下游客户热力图
        '产品研发': '/product-research',     // 产品研发数字化 · 双研发中心协同
        '生产安全': '/safety-flow',          // 酪蛋白生产安全流程（10 台核心设备）
        '工艺流程': '/process-flow',         // 酪蛋白生产工艺流程（16 主线 + 3 分支）
      };
      if (SUB_PAGE[item]) {
        this.$router.push({ path: SUB_PAGE[item], query: { from: 'industry-chain' } });
        return;
      }
      // 兜底：其余可点击项进入占位下级页
      this.$router.push('/chain-sub/' + encodeURIComponent(item));
    },
  },
};
</script>

<style lang="less" scoped>
.industry-chain {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--page-bg, transparent);
}

.ic-page {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* 顶部预留 --screen-margin 给大屏标题；底部预留给导航条（避免设备层被压住） */
  padding: calc(var(--screen-margin, 289px) + 10px) 60px 220px;
}

.ic-body {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 24px;
}

/* ---------- 左侧层级标签 ---------- */
.ic-side {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  flex-shrink: 0;
}

.ic-side-left {
  width: 256px;
}

/* ===== 左右竖标题框：科技风（斜切角描边 + 蜂窝纹理底 + 边缘发光条） ===== */
.ic-layer-tag,
.ic-guard {
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(80, 180, 255, 0.9);
  clip-path: polygon(24px 0, calc(100% - 24px) 0, 100% 24px, 100% calc(100% - 24px), calc(100% - 24px) 100%, 24px 100%, 0 calc(100% - 24px), 0 24px);
  filter: drop-shadow(0 0 16px rgba(0, 229, 255, 0.45)) brightness(1);
}

.ic-layer-tag::before,
.ic-guard::before {
  content: '';
  position: absolute;
  inset: 4px;
  clip-path: polygon(22px 0, calc(100% - 22px) 0, 100% 22px, 100% calc(100% - 22px), calc(100% - 22px) 100%, 22px 100%, 0 calc(100% - 22px), 0 22px);
  background:
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='98' viewBox='0 0 28 49'%3E%3Cg fill='none' stroke='%2360aaff' stroke-opacity='0.28'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v14.2M25 17.9v14.2M13.99 40.25v-15M3 17.9l11-6.35M25 17.9l-11-6.35M14 25.65l11 6.35M14 25.65l-11 6.35z'/%3E%3C/g%3E%3C/svg%3E") center / 56px 98px,
    linear-gradient(180deg, rgba(13, 45, 105, 0.94), rgba(9, 34, 84, 0.94));
}

.ic-layer-tag::after,
.ic-guard::after {
  content: '';
  position: absolute;
  left: 16%;
  right: 16%;
  top: 0;
  bottom: 0;
  pointer-events: none;
  background:
    linear-gradient(90deg, transparent, rgba(140, 230, 255, 0.95), transparent) left top / 100% 6px no-repeat,
    linear-gradient(90deg, transparent, rgba(140, 230, 255, 0.95), transparent) left bottom / 100% 6px no-repeat;
  filter: drop-shadow(0 0 10px rgba(0, 229, 255, 0.9));
}

/* 竖排文字：外层容器负责「从右到左」排布多列，每列内部才是真正的竖排。
   字多（如 MOM制造运营系统）且层高不足时，pinSides 会自动拆成两列 */
.ic-layer-tag-text,
.ic-guard-text {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: center;
  gap: 8px;
  /* 字号/字距由 pinSides 按「设备层」基准统一注入（继承容器） */
  font-size: inherit;
  letter-spacing: inherit;
  color: #fff;
  font-weight: 600;
  position: relative;
  z-index: 1;
}

.ic-vcol {
  writing-mode: vertical-rl;
  white-space: nowrap;
}

/* ---------- 中间主区 ---------- */
.ic-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ic-layer {
  min-height: 0;
  background: rgba(10, 40, 96, 0.35);
  border: 2px solid rgba(0, 229, 255, 0.28);
  border-radius: 16px;
  box-shadow: inset 0 0 60px rgba(0, 120, 255, 0.08);
  display: flex;
  padding: 14px 22px;
}

/* 层间双向箭头 */
.ic-arrows {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 18px;

  .a-line {
    width: 8px;
    flex: 1;
    background: linear-gradient(180deg, #42AAFF, #0E7BE0);
  }

  .a-up {
    width: 0;
    height: 0;
    border-left: 16px solid transparent;
    border-right: 16px solid transparent;
    border-bottom: 18px solid #42AAFF;
  }

  .a-down {
    width: 0;
    height: 0;
    border-left: 16px solid transparent;
    border-right: 16px solid transparent;
    border-top: 18px solid #42AAFF;
  }
}

/* 1 智慧决策 */
.ic-decision {
  align-items: center;
  padding-top: 6px;
  padding-bottom: 6px;
}

.ic-decision-row {
  flex: 1;
  display: flex;
  align-items: stretch;
  gap: 28px;
}

.ic-decision-item {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  /* 图标缩小后层内会出现富余高度，整体居中避免偏向一侧留白 */
  justify-content: center;
}

.ic-decision-card {
  /* 固定高度、由父级居中：卡片不再随层高拉伸，保持与其它版块一致的块面比例 */
  flex: 0 0 100px;
  box-sizing: border-box;
  background: linear-gradient(180deg, rgba(28, 82, 184, 0.75), rgba(15, 52, 128, 0.75));
  border: 2px solid rgba(66, 170, 255, 0.55);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;

  .ic-decision-name {
    color: #fff;
    font-size: 42px;
    font-weight: 600;
    letter-spacing: 4px;
    white-space: nowrap;
  }
}

/* 2 系统集成 */
.ic-system {
  align-items: center;
}

.ic-system-row {
  flex: 1;
  display: flex;
  gap: 50px;
}

.ic-system-bar {
  flex: 1;
  background: linear-gradient(180deg, #1d5fd8, #123a9e);
  border: 2px solid rgba(90, 180, 255, 0.6);
  border-radius: 12px;
  color: #fff;
  font-size: 40px;
  font-weight: 600;
  letter-spacing: 5px;
  text-align: center;
  box-sizing: border-box;
  height: 88px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 3 MOM */
.ic-mom {
  align-items: stretch;
}

.ic-mom-row {
  flex: 1;
  display: flex;
  gap: 16px;
}

.ic-mom-group {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ic-mom-title {
  flex-shrink: 0;
  background: linear-gradient(180deg, #1d5fd8, #123a9e);
  border: 2px solid rgba(90, 180, 255, 0.6);
  border-radius: 12px;
  color: #fff;
  font-size: 42px;
  font-weight: 600;
  letter-spacing: 3px;
  text-align: center;
  box-sizing: border-box;
  height: 70px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.ic-mom-cols {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 14px;
}

.ic-mom-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 通用小项（白底蓝字） */
.ic-cell {
  flex: 1;
  min-height: 0;
  background: linear-gradient(180deg, rgba(165, 212, 255, 0.95), rgba(105, 175, 255, 0.92));
  border: 2px solid rgba(90, 165, 250, 0.7);
  border-radius: 10px;
  color: #0d2f78;
  font-size: 44px;
  /* 关键：单元格高度由 flex 推导（较矮），若沿用继承行高(≈1.43)文字会撑破格子，
     这里收紧行高，保证文字完整落在格子内不被裁切 */
  line-height: 1.05;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  letter-spacing: 1px;
}

/* 可点击项：青色高亮 */
.ic-cell.clickable,
.ic-decision-card.clickable {
  background: linear-gradient(180deg, #00E5FF, #00b8d4);
  color: #04264a;
  box-shadow: 0 0 28px rgba(0, 229, 255, 0.55);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    filter: brightness(1.15);
    box-shadow: 0 0 48px rgba(0, 229, 255, 0.85);
  }
}

/* 4 平台层 */
.ic-platform {
  align-items: stretch;
  gap: 40px;
}

.ic-platform-brand {
  flex-shrink: 0;
  align-self: center;
  color: #4FC3FF;
  font-size: 48px;
  font-weight: 700;
  letter-spacing: 5px;
  line-height: 1.35;
  text-shadow: 0 0 32px rgba(0, 180, 255, 0.6);
}

.ic-platform-right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 32px;
  justify-content: center;
}

.ic-platform-row {
  display: flex;
  gap: 20px;

  .ic-cell-plain {
    flex: 1;
    box-sizing: border-box;
    height: 80px;
    padding: 0;
  }
}

.ic-platform-infra {
  background: linear-gradient(90deg, rgba(29, 95, 216, 0.9), rgba(18, 58, 158, 0.9));
  border: 2px solid rgba(90, 180, 255, 0.5);
  border-radius: 12px;
  color: #fff;
  font-size: 44px;
  letter-spacing: 3px;
  text-align: center;
  box-sizing: border-box;
  height: 80px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 5 网络传输 */
.ic-network {
  align-items: center;
  background: linear-gradient(90deg, rgba(20, 70, 160, 0.25), rgba(40, 120, 220, 0.35), rgba(20, 70, 160, 0.25));
}

.ic-network-row {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 20px;
}

.ic-net-item {
  color: #fff;
  font-size: 40px;
  font-weight: 600;
  letter-spacing: 3px;
  white-space: nowrap;
  text-shadow: 0 0 24px rgba(0, 200, 255, 0.6);
  padding: 16px 40px;
  border: 3px solid rgba(127, 243, 255, 0.9);
  border-top-left-radius: 52% 58%;
  border-top-right-radius: 48% 60%;
  border-bottom-right-radius: 55% 40%;
  border-bottom-left-radius: 45% 42%;
  background: rgba(0, 120, 200, 0.16);
  box-shadow: 0 0 40px rgba(0, 229, 255, 0.6), inset 0 0 32px rgba(0, 229, 255, 0.22);
}

.ic-net-item:nth-child(2n) {
  border-top-left-radius: 48% 55%;
  border-top-right-radius: 52% 45%;
  border-bottom-right-radius: 45% 58%;
  border-bottom-left-radius: 55% 42%;
}

.ic-net-item:nth-child(3n) {
  border-top-left-radius: 55% 42%;
  border-top-right-radius: 45% 58%;
  border-bottom-right-radius: 50% 45%;
  border-bottom-left-radius: 50% 55%;
}

/* 6 设备层 */
.ic-device {
  align-items: center;
}

.ic-device-row {
  flex: 1;
  display: flex;
  gap: 20px;

  .ic-cell-plain {
    flex: 1;
    box-sizing: border-box;
    /* 高度自适应填满设备层，避免上下留空 */
    height: auto;
    min-height: 84px;
    padding: 0;
  }
}

/* ---------- 右侧安全控制 ---------- */
.ic-side-right {
  width: 224px;
}

/* 注：.ic-guard-text 的基础样式已与 .ic-layer-tag-text 合并定义于上方 */

/* ===== 科技感 HUD 角标 ===== */
.ic-decision-card,
.ic-system-bar,
.ic-mom-title,
.ic-cell,
.ic-platform-infra {
  position: relative;
  box-sizing: border-box;
  border-radius: 4px;
  border: 4px solid rgba(0, 229, 255, 0.85);
  box-shadow: 0 0 36px rgba(0, 229, 255, 0.4), inset 0 0 44px rgba(0, 229, 255, 0.12);
}

.ic-decision-card::before,
.ic-system-bar::before,
.ic-mom-title::before,
.ic-cell::before,
.ic-platform-infra::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(#7FF3FF, #7FF3FF) left top     / 60px 8px no-repeat,
    linear-gradient(#7FF3FF, #7FF3FF) left top     / 8px 60px no-repeat,
    linear-gradient(#7FF3FF, #7FF3FF) right top    / 60px 8px no-repeat,
    linear-gradient(#7FF3FF, #7FF3FF) right top    / 8px 60px no-repeat,
    linear-gradient(#7FF3FF, #7FF3FF) left bottom  / 60px 8px no-repeat,
    linear-gradient(#7FF3FF, #7FF3FF) left bottom  / 8px 60px no-repeat,
    linear-gradient(#7FF3FF, #7FF3FF) right bottom / 60px 8px no-repeat,
    linear-gradient(#7FF3FF, #7FF3FF) right bottom / 8px 60px no-repeat;
  filter: drop-shadow(0 0 12px rgba(0, 229, 255, 0.9));
}

/* ===== 悬停突显光亮 ===== */
.ic-decision-card,
.ic-system-bar,
.ic-mom-title,
.ic-cell,
.ic-platform-infra,
.ic-net-item,
.ic-layer-tag,
.ic-guard {
  transition: box-shadow 0.25s ease, border-color 0.25s ease, filter 0.25s ease;
}

.ic-decision-card:hover,
.ic-system-bar:hover,
.ic-mom-title:hover,
.ic-cell:hover,
.ic-platform-infra:hover {
  border-color: rgba(127, 243, 255, 1);
  box-shadow: 0 0 64px rgba(0, 229, 255, 0.7), inset 0 0 56px rgba(0, 229, 255, 0.28);
  filter: brightness(1.15);
}

.ic-net-item:hover {
  box-shadow: 0 0 68px rgba(0, 229, 255, 0.85), inset 0 0 48px rgba(0, 229, 255, 0.35);
  filter: brightness(1.2);
}

.ic-layer-tag:hover,
.ic-guard:hover {
  filter: drop-shadow(0 0 32px rgba(0, 229, 255, 0.85)) brightness(1.25);
}

/* ===== 平台层/设备层标题底框：与「生产建模」MOM 标题同款 ===== */
.ic-cell-plain {
  background: linear-gradient(180deg, #1d5fd8, #123a9e);
  color: #fff;
}
</style>
